import {
	City,
	ContractSuretyType,
	Dtos,
	formatAddress,
	getAllowedIndemnitySignatureMethods,
	getValidAddressSchema,
	IndemnityAgreementSignatureMethod,
	NameSuffix,
	StreetAddress,
	SuretyBondIssuanceMethod,
	USStateOrTerritory,
	ZipCode,
} from "@inrev/common";
import { Option } from "@inrev/core";
import {
	deriveIndemnitors,
	type SuretyAccount,
	useFetchAccountCompanies,
	useFetchAccountIndividuals,
	useFetchAccountLatestIndemnityAgreement,
} from "@portal/api/account";
import { SuretyBond, useFetchBond } from "@portal/api/bond/agent";
import {
	BondIssuanceFormData,
	BondRequest,
	QuoteBondForm,
	useCloseBondRequest,
	useFetchQuoteBondForm,
	useIssueBond,
	ValidatedBondIssuanceFormData,
} from "@portal/api/bond-request/agent";
import { getBondIssuanceFormValidationSchema } from "@portal/api/bond-request/agent/validation";
import {
	FormField,
	FormFieldGroup,
	FormItemLabel,
	FormRow,
	FormSection,
} from "@portal/components/form";
import {
	FormAddressAutocomplete,
	FormDatePicker,
	FormDropdown,
	FormInput,
} from "@portal/components/form/fields";
import { ConfirmationModal } from "@portal/components/layout/ConfirmationModal";
import { LoadingModal } from "@portal/components/layout/LoadingModal";
import { Modal } from "@portal/components/layout/Modal";
import { cn } from "@portal/components/lib/utils";
import { Banner, Button, Card, Icon, Text } from "@portal/components/primitives";
import { MoreActionsMenu } from "@portal/components/ui/MoreActionsMenu";
import { SpinnerCheck } from "@portal/components/ui/SpinnerCheck";
import { useObligeeRoleOptions } from "@portal/hooks/useObligeeRoleOptions";
import { useEffect, useMemo, useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { Tooltip } from "react-tooltip";
import { useAddressValidation } from "../../../../../api";
import { useScrollToId } from "../../../../../utils";
import { stripEmptyResolver, typedZodResolver } from "../../../../../utils/form";
import { ClosedReasonModal } from "../../../../shared/account/ClosedReasonModal";
import { BondRequestLayout } from "../../../../shared/request/BondRequestLayout";
import { WetSignatureDisclosureCard } from "../../../account/shared/WetSignatureDisclosureCard";
import { AccountIndemnitorList } from "../../../account/status/submitted/underwritten/indemnity/components/AccountIndemnitorList";
import { IndemnityAgreementStatusIndicator } from "../../../account/status/submitted/underwritten/indemnity/components/IndemnityAgreementStatusIndicator";
import { IndemnityAgreementSectionLayout } from "../../../account/status/submitted/underwritten/indemnity/layout/IndemnityAgreementLayout";

type AcceptedBondRequestViewProps = {
	request: BondRequest;
	account: SuretyAccount;
};

type AcceptedBondRequestChildViewProps = AcceptedBondRequestViewProps & {
	defaultFormProps?: DefaultFormProps;
};

type IssuanceFormContext = {
	allowedIssuanceMethods: Dtos.SuretyBondIssuance.Get.Response["allowedIssuanceMethods"];
	indemnityAgreement: Option<Dtos.SuretyAccount.IndemnityAgreement.Get.Response>;
	indemnitors: Dtos.SuretyAccount.IndemnityAgreement.Get.Response["indemnitors"];
	allowedSignatureMethods: IndemnityAgreementSignatureMethod[];
};

const draftBondRequestBondIssuanceFormResolver: Resolver<
	BondIssuanceFormData,
	IssuanceFormContext,
	ValidatedBondIssuanceFormData
> = (values, context, options) => {
	if (!context) throw new Error();

	// This is a patch to ensure that zod errors are included for empty obligee fields while we only support one obligee
	if (!!!values.bondForm.data.obligees || !!!values.bondForm.data.obligees[0]) {
		values.bondForm.data.obligees = [{} as any];
	}

	return typedZodResolver<BondIssuanceFormData, IssuanceFormContext, ValidatedBondIssuanceFormData>(
		getBondIssuanceFormValidationSchema(context),
	)(values, context, options);
};

const deriveSigningStates = (
	companies: (Dtos.Company.Get.Response | Dtos.Admin.SuretyAccount.Get.Response.Company)[],
): USStateOrTerritory[] => Array.from(new Set(companies.map((c) => c.address.state)));

const getDefaultCompanyIndemnitorFormData = (
	companyIndemnitor: Dtos.SuretyAccount.IndemnityAgreement.Get.Response["indemnitors"]["companies"][number],
) => {
	return {
		contactId: companyIndemnitor.id,
		name: companyIndemnitor.name,
		signerEmail: "",
	};
};

const getDefaultIndividualIndemnitorFormData = (
	individualIndemnitor: Dtos.SuretyAccount.IndemnityAgreement.Get.Response["indemnitors"]["individuals"][number],
) => {
	return {
		contactId: individualIndemnitor.id,
		name: individualIndemnitor.name,
		signerEmail: individualIndemnitor.signerEmail ?? "",
	};
};

const getDefaultFormData = (
	request: AcceptedBondRequestViewProps["request"],
	indemnitors: Dtos.SuretyAccount.IndemnityAgreement.Get.Response["indemnitors"],
	defaultProps: DefaultFormProps = {},
): BondIssuanceFormData => ({
	bondForm: {
		suretyType: request.suretyType,
		contractSuretyType: request.contractSuretyType,
		issuanceMethod: "",
		data: {
			contractDate: "",
			contractDescription: request.project.name ?? "",
			projectDescription: request.project.name ?? "",
			obligees: [
				{
					name: defaultProps.obligeeName ?? "",
					address: defaultProps.obligeeAddress ? formatAddress(defaultProps.obligeeAddress) : "",
					role: defaultProps.obligeeRole ?? "",
				},
			],
			principalSigner: {
				firstName: "",
				lastName: "",
				suffix: "",
				title: "",
			},
		},
	},
	indemnityAgreement: {
		suretyType: request.suretyType,
		signatureMethod: null,
		companySigners: indemnitors.companies.map((companyIndemnitor) =>
			getDefaultCompanyIndemnitorFormData(companyIndemnitor),
		),
		individualSigners: indemnitors.individuals.map((individualIndemnitor) =>
			getDefaultIndividualIndemnitorFormData(individualIndemnitor),
		),
	},
});

export const AcceptedBondRequestView = ({ request, account }: AcceptedBondRequestViewProps) => {
	const isBidToFinal = useMemo(
		() => request.contractSuretyType === ContractSuretyType.bid_to_final,
		[request.contractSuretyType],
	);
	if (isBidToFinal)
		return <AcceptedBondRequestChildViewBidToFinal request={request} account={account} />;
	return <AcceptedBondRequestChildView request={request} account={account} />;
};

type DefaultFormProps = {
	obligeeName?: string;
	obligeeRole?: SuretyBond["obligees"][number]["role"];
	obligeeAddress?: {
		street: StreetAddress;
		city: City;
		state: USStateOrTerritory;
		zip: ZipCode;
	};
};

const AcceptedBondRequestChildViewBidToFinal = ({
	request,
	account,
}: AcceptedBondRequestChildViewProps) => {
	const bidBondId =
		request.contractSuretyType === ContractSuretyType.bid_to_final ? request.bidBondId : "";
	const { bond: bidBond } = useFetchBond(bidBondId);
	const [defaultFormData, setDefaultFormData] = useState<DefaultFormProps | undefined>();

	/*
		Given this flow:
			1. Agent selects agent-issued option on a bid bond / admin enters obligee information with invalid address on the bond
			2. Agent chooses to convert the bid bond to final & chooses an issuing option other than agent-issued for the final bond
			3. We attempt to auto-populate the obligee information from the bid bond onto the final bond

		The user would experience an error after step 3. This is due to the fact that when the admin enters the information in step 1, those
		inputs do not validate the address. Despite the lack of validation, when we attempt to autopopulate that same information on the final
		bond in step 3, the fields we are attempting to populate do validate the address. This would cause errors and leave the user in a broken state.

		To avoid this, we are attempting to validate the address prior to autopopulating it onto the final bond form. If it fails validation, we omit it
		in favor of an empty value.
	*/
	useEffect(() => {
		if (bidBond === undefined) return;
		if (bidBond.obligees?.[0] === undefined) {
			setDefaultFormData({});
			return;
		}
		const obligee = bidBond.obligees[0];
		const validate = async () => {
			const result = await getValidAddressSchema({ allowNull: false }).safeParseAsync(
				obligee.address,
			);
			if (result.success) {
				setDefaultFormData({
					obligeeName: obligee.name,
					obligeeAddress: result.data,
					obligeeRole: obligee.role,
				});
			} else {
				setDefaultFormData({
					obligeeName: obligee.name,
					obligeeRole: obligee.role,
				});
			}
		};
		validate();
	}, [bidBond]);

	if (defaultFormData === undefined) return <LoadingModal />;

	return (
		<AcceptedBondRequestChildView
			request={request}
			account={account}
			defaultFormProps={defaultFormData}
		/>
	);
};

const AcceptedBondRequestChildView = ({
	request,
	account,
	defaultFormProps,
}: AcceptedBondRequestChildViewProps) => {
	const { latestIndemnityAgreement, latestIndemnityAgreementIsLoading } =
		useFetchAccountLatestIndemnityAgreement(request.suretyAccountId);
	const { quoteBondForm, quoteBondFormIsLoading } = useFetchQuoteBondForm(
		request.suretyAccountId,
		request.id,
	);
	const { companies, companiesIsLoading } = useFetchAccountCompanies(account.id);
	const { individuals, individualsIsLoading } = useFetchAccountIndividuals(account.id);

	// The issuance form captures indemnitors into react-hook-form defaultValues on first
	// render, so it must not mount until companies and individuals have resolved.
	if (
		latestIndemnityAgreementIsLoading ||
		quoteBondFormIsLoading ||
		companiesIsLoading ||
		individualsIsLoading ||
		!companies ||
		!individuals
	) {
		return <LoadingModal />;
	}

	return (
		<AcceptedBondRequestForm
			request={request}
			account={account}
			defaultFormProps={defaultFormProps}
			latestIndemnityAgreement={latestIndemnityAgreement}
			quoteBondForm={quoteBondForm}
			companies={companies}
			individuals={individuals}
		/>
	);
};

type AcceptedBondRequestFormProps = AcceptedBondRequestChildViewProps & {
	latestIndemnityAgreement: Option<Dtos.SuretyAccount.IndemnityAgreement.Get.Response>;
	quoteBondForm: QuoteBondForm | undefined;
	companies: (Dtos.Company.Get.Response | Dtos.Admin.SuretyAccount.Get.Response.Company)[];
	individuals: Dtos.Individual.Get.Response[];
};

const AcceptedBondRequestForm = ({
	request,
	account,
	defaultFormProps,
	latestIndemnityAgreement,
	quoteBondForm,
	companies,
	individuals,
}: AcceptedBondRequestFormProps) => {
	const obligeeRoleOptions = useObligeeRoleOptions();
	const indemnitors = useMemo(
		() => deriveIndemnitors({ companies, individuals }, latestIndemnityAgreement),
		[companies, individuals, latestIndemnityAgreement],
	);
	const allowedSignatureMethods = useMemo(
		() =>
			latestIndemnityAgreement?.allowedSignatureMethods ??
			getAllowedIndemnitySignatureMethods(deriveSigningStates(companies)),
		[latestIndemnityAgreement, companies],
	);

	const formContext = useMemo<IssuanceFormContext>(
		() => ({
			allowedIssuanceMethods: request.allowedIssuanceMethods ?? [],
			indemnityAgreement: latestIndemnityAgreement,
			indemnitors,
			allowedSignatureMethods,
		}),
		[
			request.allowedIssuanceMethods,
			latestIndemnityAgreement,
			indemnitors,
			allowedSignatureMethods,
		],
	);

	const formMethods = useForm<
		BondIssuanceFormData,
		IssuanceFormContext,
		ValidatedBondIssuanceFormData
	>({
		defaultValues: getDefaultFormData(request, indemnitors, defaultFormProps),
		reValidateMode: "onBlur",
		context: formContext,
		resolver: stripEmptyResolver(draftBondRequestBondIssuanceFormResolver, false, true),
	});

	const obligeeAddressValidation = useAddressValidation({
		value: formMethods.watch("bondForm.data.obligees.0.address"),
		error: formMethods.formState.errors.bondForm?.data?.obligees?.[0]?.address,
		setValue: (value: string) => formMethods.setValue("bondForm.data.obligees.0.address", value),
		setError: ({ type, message }) =>
			formMethods.setError("bondForm.data.obligees.0.address", { type, message }),
		clearError: () => formMethods.clearErrors("bondForm.data.obligees.0.address"),
	});
	const [showSubmissionConfirmation, setShowSubmissionConfirmation] = useState(false);
	const [showClosedReasonModal, setShowClosedReasonModal] = useState(false);
	const [validatedSubmitData, setValidatedSubmitData] = useState<
		ValidatedBondIssuanceFormData | undefined
	>();
	const [termsAndConditionsOpen, _setTermsAndConditionsOpen] = useState(false);
	const { closeBondRequest, closeBondRequestLoading, closeBondRequestError } = useCloseBondRequest(
		request.suretyAccountId,
		request.id,
	);
	const { issueBond, issueBondIsLoading } = useIssueBond(request, latestIndemnityAgreement);
	const domReadyRef = useScrollToId("start");

	useEffect(() => {
		if (validatedSubmitData !== undefined) setShowSubmissionConfirmation(true);
		else if (showSubmissionConfirmation) setShowSubmissionConfirmation(false);
	}, [validatedSubmitData]);

	const onSubmit = (data: ValidatedBondIssuanceFormData) => {
		setValidatedSubmitData(data);
	};

	const onSubmitConfirm = () => {
		if (validatedSubmitData === undefined) throw new Error();
		setValidatedSubmitData(undefined);
		document
			.getElementById("bond_request_card")
			?.scrollIntoView({ block: "center", behavior: "instant" });
		issueBond(validatedSubmitData);
	};

	const onSubmitCancel = () => {
		setValidatedSubmitData(undefined);
	};
	const indemnityAgreement = latestIndemnityAgreement;
	const companyIndemnitors = formMethods.watch("indemnityAgreement.companySigners");
	const individualIndemnitors = formMethods.watch("indemnityAgreement.individualSigners");
	const indemnityAgreementSignatureMethod = formMethods.watch("indemnityAgreement.signatureMethod");
	const bondFormIssuanceMethod = formMethods.watch("bondForm.issuanceMethod");
	const hideIssuanceFormBody =
		bondFormIssuanceMethod === "" ||
		bondFormIssuanceMethod === "agent" ||
		bondFormIssuanceMethod === "physical";

	// Check if bond form is selected
	const bondFormSelected = useMemo(() => {
		const hasTemplate = !!quoteBondForm?.templateId;
		const hasUpload = !!quoteBondForm?.upload?.files && quoteBondForm.upload.files.length > 0;
		return hasTemplate || hasUpload;
	}, [quoteBondForm]);

	const disabledTooltipMessage = useMemo(() => {
		const missingIssuanceMethod = !bondFormIssuanceMethod;
		const missingBondForm = !bondFormSelected;
		const missingIndemnitySignatureMethod =
			!indemnityAgreementSignatureMethod && !indemnityAgreement;

		const missingItems: string[] = [];
		if (missingIssuanceMethod) missingItems.push("an issuance method");
		if (missingBondForm) missingItems.push("a bond form");
		if (missingIndemnitySignatureMethod)
			missingItems.push("an indemnity agreement signature method");

		if (missingItems.length === 0) return "";
		if (missingItems.length === 1) return `Select ${missingItems[0]} below to issue the bond`;
		if (missingItems.length === 2)
			return `Select ${missingItems[0]} and ${missingItems[1]} below to issue the bond`;
		return `Select ${missingItems[0]}, ${missingItems[1]}, and ${missingItems[2]} below to issue the bond`;
	}, [bondFormIssuanceMethod, bondFormSelected, indemnityAgreementSignatureMethod]);

	const signatureMethodOptions = useMemo(() => {
		return IndemnityAgreementSignatureMethod.options().filter((opt) =>
			allowedSignatureMethods.includes(opt.value),
		);
	}, [allowedSignatureMethods]);

	const indemnityAgreementSectionFormBody = useMemo(() => {
		if (indemnityAgreementSignatureMethod === IndemnityAgreementSignatureMethod.electronic) {
			return (
				<div className="flex flex-col space-y-[45px]">
					<div className="flex flex-col space-y-[15px] w-full h-fit min-h-fit">
						{companyIndemnitors.map((company, index) => (
							<FormFieldGroup key={index} className="py-[8px] rounded-sm space-y-[10px]">
								<div className="flex items-center space-x-[10px] font-semibold">
									<span className="text-gray-400 text-[13px]">{index + 1}.</span>
									<span className="text-primary text-[14px]">{company.name}</span>
								</div>
								<FormField condensed className="px-[18px]">
									<FormItemLabel condensed>Signer Email</FormItemLabel>
									<FormInput
										control={formMethods.control}
										name={`indemnityAgreement.companySigners.${index}.signerEmail`}
										className="bg-white"
										compact
									/>
								</FormField>
							</FormFieldGroup>
						))}
						{individualIndemnitors.map((individual, index) => (
							<FormFieldGroup
								key={indemnitors.companies.length + index}
								className="py-[8px] rounded-sm space-y-[10px]"
							>
								<div className="flex items-center space-x-[10px] font-semibold">
									<span className="text-gray-400 text-[13px]">
										{indemnitors.companies.length + index + 1}.
									</span>
									<span className="text-primary text-[14px]">{individual.name}</span>
								</div>
								<FormField condensed className="px-[18px]">
									<FormItemLabel condensed>Email</FormItemLabel>
									<FormInput
										control={formMethods.control}
										name={`indemnityAgreement.individualSigners.${index}.signerEmail`}
										className="bg-white"
										compact
									/>
								</FormField>
							</FormFieldGroup>
						))}
					</div>
				</div>
			);
		}
		if (indemnityAgreementSignatureMethod === IndemnityAgreementSignatureMethod.physical) {
			return (
				<>
					<WetSignatureDisclosureCard />
				</>
			);
		}
		// Return undefined instead of null to avoid styling an empty section in the layout
		return undefined;
	}, [indemnityAgreementSignatureMethod]);

	const issueBondDisabled =
		!bondFormIssuanceMethod ||
		!bondFormSelected ||
		(!indemnityAgreement && !indemnityAgreementSignatureMethod);

	const issueBondCta =
		bondFormIssuanceMethod !== "" ? (
			<div className="flex flex-col items-center">
				<Button
					type="submit"
					color="primary"
					variant="solid"
					className="w-[165px]"
					disabled={issueBondDisabled}
					data-tooltip-id="issue-bond-disabled-tooltip-bottom"
					data-tooltip-content={disabledTooltipMessage}
					trackingId="bond-request-agent-issue-bond-bottom"
				>
					Issue Bond
				</Button>
				{disabledTooltipMessage && <Tooltip id="issue-bond-disabled-tooltip-bottom" />}
				{formMethods.formState.isSubmitted &&
					Object.keys(formMethods.formState.errors).length > 0 && (
						<span className="text-[13px] text-red-500 leading-[18px] self-center text-center">
							Please review form errors
						</span>
					)}
			</div>
		) : null;

	return (
		<BondRequestLayout
			request={request}
			account={account}
			termsAndConditionsOpen={termsAndConditionsOpen}
			actionButtons={
				<div className="flex flex-col space-y-[4px] max-w-[165px]">
					<Button
						onClick={formMethods.handleSubmit(onSubmit)}
						color="primary"
						variant="solid"
						disabled={
							!bondFormIssuanceMethod ||
							!bondFormSelected ||
							(!indemnityAgreement && !indemnityAgreementSignatureMethod)
						}
						data-tooltip-id="issue-bond-disabled-tooltip"
						data-tooltip-content={disabledTooltipMessage}
						trackingId="bond-request-agent-issue-bond-sidebar"
					>
						Issue Bond
					</Button>
					{disabledTooltipMessage && <Tooltip id="issue-bond-disabled-tooltip" />}
					{formMethods.formState.isSubmitted &&
						Object.keys(formMethods.formState.errors).length > 0 && (
							<span className="text-[13px] text-red-500 leading-[18px] self-center text-center">
								Please review form errors
							</span>
						)}
					<MoreActionsMenu
						items={[
							{
								label: "View Terms & Conditions",
								icon: <Icon type="eye" size="mini" />,
								onClick: () => _setTermsAndConditionsOpen(true),
								trackingId: "bond-request-agent-view-terms",
							},
							{
								label: "Close Bond Request",
								icon: <Icon type="minus-circle" size="mini" />,
								onClick: () => setShowClosedReasonModal(true),
								trackingId: "bond-request-agent-close",
							},
						]}
						panelClassName="mt-[10px]"
					/>
				</div>
			}
		>
			<form
				id="bond_request_issuance"
				className="w-full flex flex-col space-y-[30px]"
				onSubmit={formMethods.handleSubmit(onSubmit)}
			>
				<div className="w-full flex flex-col space-y-[40px]">
					{!indemnityAgreement && (
						<IndemnityAgreementSectionLayout
							status={
								<IndemnityAgreementStatusIndicator
									status="incomplete"
									signatureMethod={indemnityAgreementSignatureMethod}
								/>
							}
							signatureMethod={
								<FormDropdown
									compact
									control={formMethods.control}
									name="indemnityAgreement.signatureMethod"
									options={signatureMethodOptions}
									placeholder="Select one"
									anchor="bottom start"
								/>
							}
							body={indemnityAgreementSectionFormBody}
							requiredIndemnitors={
								<AccountIndemnitorList indemnitors={indemnitors} showESignatureDetails={false} />
							}
							domReadyRef={domReadyRef}
						/>
					)}
					<Card elevation="raised">
						<Card.Header
							className={cn(
								"h-fit flex-col items-stretch justify-start gap-[22px] px-6 py-4",
								hideIssuanceFormBody && "border-b-0 rounded-b-[inherit]",
							)}
						>
							<Card.Title>Bond Form Issuance Method</Card.Title>
							<FormField condensed>
								<FormItemLabel
									condensed
									required
									className="mb-[3px] text-gray-800/95 text-[14.75px] font-[485]"
								>
									How should this bond form be issued?
								</FormItemLabel>
								<FormDropdown
									compact
									control={formMethods.control}
									name="bondForm.issuanceMethod"
									options={(
										[
											{ value: SuretyBondIssuanceMethod.electronic, label: "Electronic" },
											{
												value: SuretyBondIssuanceMethod.physical,
												label: "inRev Issue - Wet Signatures, Raised Seals",
											},
											{ value: SuretyBondIssuanceMethod.agent, label: "Agent Issue" },
										] as const
									).filter((option) =>
										(request.allowedIssuanceMethods ?? []).includes(option.value),
									)}
									placeholder="Select one"
									errorMessage="Required"
									anchor="bottom start"
								/>
							</FormField>
							{bondFormIssuanceMethod === "agent" && (
								<Banner tone="info">
									<Banner.Leading>
										<Icon type="information-circle" size="medium" className="text-brand-accent" />
									</Banner.Leading>
									<Banner.Content>
										<Text type="body" color="primary">
											By selecting{" "}
											<Text type="body-strong" color="primary">
												Agent Issue
											</Text>
											, you are responsible for filling out the bond form, applying your physical
											seal, and sending the bond form to your client
										</Text>
									</Banner.Content>
								</Banner>
							)}
							{bondFormIssuanceMethod === "physical" && (
								<Banner tone="info">
									<Banner.Leading>
										<Icon type="information-circle" size="medium" className="text-brand-accent" />
									</Banner.Leading>
									<Banner.Content className="flex flex-col gap-4">
										<Text type="body" color="primary">
											By selecting{" "}
											<Text type="body-strong" color="primary">
												inRev Issue
											</Text>
											, inRev will issue the bond with wet signatures and raised seals. A scanned
											copy of the executed bond will be provided to you.
										</Text>
										<Text type="body" color="primary">
											To receive the original hard copy:
										</Text>
										<ul className="list-disc pl-5 space-y-1">
											<li>
												<Text type="body" color="primary">
													Please confirm the overnight delivery address in the{" "}
													<Text type="body-strong" color="primary">
														Messages
													</Text>{" "}
													below or upload a shipping label to the{" "}
													<Text type="body-strong" color="primary">
														Attachments
													</Text>
													.
												</Text>
											</li>
											<li>
												<Text type="body" color="primary">
													For any additional requests, please send us a{" "}
													<Text type="body-strong" color="primary">
														Message
													</Text>
													.
												</Text>
											</li>
										</ul>
									</Banner.Content>
								</Banner>
							)}
							{hideIssuanceFormBody && issueBondCta}
						</Card.Header>
						{!hideIssuanceFormBody && (
							<Card.Body
								surface="gray"
								className="flex flex-col space-y-[40px] py-[35px] px-[45px] pb-[50px]"
							>
								<FormFieldGroup condensed>
									<FormField condensed>
										<FormItemLabel condensed required>
											Project Description
										</FormItemLabel>
										<span className="!my-[5px] text-[14px] text-tertiary italic">
											As it should appear on the bond form
										</span>
										<FormInput
											control={formMethods.control}
											name={
												request.contractSuretyType === "bid"
													? `bondForm.data.projectDescription`
													: `bondForm.data.contractDescription`
											}
											className="bg-white"
											compact
										/>
									</FormField>
									{request.contractSuretyType === "final" && (
										<FormField condensed>
											<FormItemLabel condensed required>
												Contract Date
											</FormItemLabel>
											<FormDatePicker
												control={formMethods.control}
												name={`bondForm.data.contractDate`}
												className="bg-white"
												compact
											/>
										</FormField>
									)}
								</FormFieldGroup>
								<FormSection header="Obligee">
									<FormFieldGroup condensed>
										<FormRow>
											<FormField condensed>
												<FormItemLabel condensed required>
													Name
												</FormItemLabel>
												<FormInput
													control={formMethods.control}
													name={`bondForm.data.obligees.0.name`}
													className="bg-white"
													compact
												/>
											</FormField>
											<FormField condensed className="w-[50px] max-w-[225px]">
												<FormItemLabel condensed required>
													Role
												</FormItemLabel>
												<FormDropdown
													control={formMethods.control}
													name={"bondForm.data.obligees.0.role"}
													buttonClassName="bg-white"
													options={obligeeRoleOptions}
													placeholder="Select one"
													compact
													anchor="bottom start"
												/>
											</FormField>
										</FormRow>
										<FormField condensed>
											<FormItemLabel condensed required>
												Address
											</FormItemLabel>
											<FormAddressAutocomplete
												control={obligeeAddressValidation.addressFieldControl}
												className="bg-white"
												compact
											/>
										</FormField>
									</FormFieldGroup>
								</FormSection>
								<FormSection
									header="Signer"
									subHeader="If available"
									subHeaderClassName="text-tertiary italic"
								>
									<FormFieldGroup condensed>
										<FormRow>
											<FormField condensed>
												<FormItemLabel condensed>First Name</FormItemLabel>
												<FormInput
													control={formMethods.control}
													name={`bondForm.data.principalSigner.firstName`}
													className="bg-white"
													compact
												/>
											</FormField>
											<FormField condensed>
												<FormItemLabel condensed>Last Name</FormItemLabel>
												<FormInput
													control={formMethods.control}
													name={`bondForm.data.principalSigner.lastName`}
													className="bg-white"
													compact
												/>
											</FormField>
											<FormField className="w-[100px] max-w-[100px]" condensed>
												<FormItemLabel condensed>Suffix</FormItemLabel>
												<FormDropdown
													control={formMethods.control}
													name="bondForm.data.principalSigner.suffix"
													options={Object.values(NameSuffix).map((suffix) => ({
														value: suffix,
														label: suffix,
													}))}
													buttonClassName="bg-white"
													compact
													anchor="bottom start"
												/>
											</FormField>
										</FormRow>
										<FormField condensed>
											<FormItemLabel condensed>Title</FormItemLabel>
											<FormInput
												control={formMethods.control}
												name={`bondForm.data.principalSigner.title`}
												className="bg-white"
												compact
											/>
										</FormField>
									</FormFieldGroup>
								</FormSection>
								{issueBondCta}
							</Card.Body>
						)}
					</Card>
				</div>
				{showSubmissionConfirmation && (
					<ConfirmationModal
						onConfirm={onSubmitConfirm}
						onCancel={onSubmitCancel}
						message="Are you sure you want to issue this bond?"
					/>
				)}
				{issueBondIsLoading && (
					<Modal>
						<div
							className={
								"py-[35px] px-[35px] bg-white flex flex-col justify-center rounded-md shadow-lg space-y-[20px]"
							}
						>
							<div className="flex items-center space-x-[10px]">
								<SpinnerCheck spinning={true} className="bg-violet-700" />
								<div className="flex-1">Issuing your bond</div>
							</div>
						</div>
					</Modal>
				)}
				{showClosedReasonModal && (
					<ClosedReasonModal
						closeFunction={closeBondRequest}
						closeFunctionError={closeBondRequestError}
						isClosing={closeBondRequestLoading}
						onDismiss={() => setShowClosedReasonModal(false)}
						closingAction="close"
					/>
				)}
			</form>
			{closeBondRequestLoading && (
				<Modal>
					<LoadingModal />
				</Modal>
			)}
		</BondRequestLayout>
	);
};
